/**
 * Analytics helpers for GTM, Facebook Pixel, TikTok Pixel, and TikTok Events API (server-side)
 */

import { supabase } from "@/integrations/supabase/client";

interface ProductData {
  id: string;
  name: string;
  price: number;
  quantity: number;
  category?: string;
}

// Push event to GTM dataLayer
const pushToDataLayer = (event: string, data: Record<string, any>) => {
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ event, ...data });
};

// Fire Facebook Pixel event
const fireFbPixel = (event: string, data: Record<string, any>) => {
  const w = window as any;
  if (typeof w.fbq === "function") {
    w.fbq("track", event, data);
  }
};

// Fire TikTok Pixel event
const fireTtqPixel = (event: string, data: Record<string, any>) => {
  const w = window as any;
  if (typeof w.ttq?.track === "function") {
    w.ttq.track(event, data);
  }
};

// Fire TikTok server-side event (non-blocking)
const fireTtqServer = (
  event: string,
  properties: Record<string, any>,
  userInfo?: { email?: string; phone?: string; userId?: string }
) => {
  // Fire and forget — don't await
  supabase.functions
    .invoke("tiktok-events", {
      body: {
        event,
        event_id: `${event}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        user: {
          email: userInfo?.email,
          phone: userInfo?.phone,
          external_id: userInfo?.userId,
          user_agent: navigator.userAgent,
        },
        properties: {
          ...properties,
          url: window.location.href,
        },
      },
    })
    .then((res) => {
      if (res.error) console.warn("TikTok server event failed:", res.error);
    })
    .catch(() => {});
};

/* ─── Existing Events ─── */

export const trackInitiateCheckout = (
  products: ProductData[],
  totalValue: number,
  currency = "ILS"
) => {
  const contents = products.map((p) => ({
    id: p.id,
    quantity: p.quantity,
    item_price: p.price,
  }));
  const contentNames = products.map((p) => p.name);

  // GTM
  pushToDataLayer("InitiateCheckout", {
    ecommerce: {
      currency,
      value: totalValue,
      items: products.map((p) => ({
        item_id: p.id,
        item_name: p.name,
        price: p.price,
        quantity: p.quantity,
        item_category: p.category || "",
      })),
    },
  });

  // Facebook Pixel
  fireFbPixel("InitiateCheckout", {
    content_ids: products.map((p) => p.id),
    content_name: contentNames.join(", "),
    contents,
    content_type: "product",
    num_items: products.reduce((s, p) => s + p.quantity, 0),
    value: totalValue,
    currency,
  });

  // TikTok Pixel (client-side)
  fireTtqPixel("InitiateCheckout", {
    contents: products.map((p) => ({ content_id: p.id, content_type: "product", content_name: p.name, quantity: p.quantity, price: p.price })),
    value: totalValue,
    currency,
    content_type: "product",
  });

  // TikTok Events API (server-side)
  fireTtqServer("InitiateCheckout", {
    contents: products.map((p) => ({ content_id: p.id, content_type: "product", content_name: p.name, quantity: p.quantity, price: p.price })),
    value: totalValue,
    currency,
  });
};

export const trackPurchase = (
  orderId: string,
  products: ProductData[],
  totalValue: number,
  currency = "ILS"
) => {
  const contents = products.map((p) => ({
    id: p.id,
    quantity: p.quantity,
    item_price: p.price,
  }));
  const contentNames = products.map((p) => p.name);

  // GTM
  pushToDataLayer("Purchase", {
    ecommerce: {
      transaction_id: orderId,
      currency,
      value: totalValue,
      items: products.map((p) => ({
        item_id: p.id,
        item_name: p.name,
        price: p.price,
        quantity: p.quantity,
        item_category: p.category || "",
      })),
    },
  });

  // Facebook Pixel
  fireFbPixel("Purchase", {
    content_ids: products.map((p) => p.id),
    content_name: contentNames.join(", "),
    contents,
    content_type: "product",
    num_items: products.reduce((s, p) => s + p.quantity, 0),
    value: totalValue,
    currency,
  });

  // TikTok Pixel (client-side)
  fireTtqPixel("CompletePayment", {
    contents: products.map((p) => ({ content_id: p.id, content_type: "product", content_name: p.name, quantity: p.quantity, price: p.price })),
    value: totalValue,
    currency,
    content_type: "product",
  });

  // TikTok Events API (server-side)
  fireTtqServer("CompletePayment", {
    contents: products.map((p) => ({ content_id: p.id, content_type: "product", content_name: p.name, quantity: p.quantity, price: p.price })),
    value: totalValue,
    currency,
  });
};

/* ─── New TikTok Events ─── */

export const trackViewContent = (
  productId: string,
  name: string,
  price: number,
  category?: string,
  currency = "ILS"
) => {
  fireFbPixel("ViewContent", {
    content_ids: [productId],
    content_name: name,
    content_type: "product",
    content_category: category || "",
    value: price,
    currency,
  });

  fireTtqPixel("ViewContent", {
    contents: [{ content_id: productId, content_type: "product", content_name: name, quantity: 1, price }],
    value: price,
    currency,
    content_type: "product",
  });

  fireTtqServer("ViewContent", {
    contents: [{ content_id: productId, content_type: "product", content_name: name, quantity: 1, price }],
    value: price,
    currency,
  });
};

export const trackAddToCart = (
  productId: string,
  name: string,
  price: number,
  quantity = 1,
  currency = "ILS"
) => {
  fireFbPixel("AddToCart", {
    content_ids: [productId],
    content_name: name,
    content_type: "product",
    value: price * quantity,
    currency,
  });

  fireTtqPixel("AddToCart", {
    contents: [{ content_id: productId, content_type: "product", content_name: name, quantity, price }],
    value: price * quantity,
    currency,
    content_type: "product",
  });

  fireTtqServer("AddToCart", {
    contents: [{ content_id: productId, content_type: "product", content_name: name, quantity, price }],
    value: price * quantity,
    currency,
  });
};

export const trackAddToWishlist = (
  productId: string,
  name: string,
  price: number,
  currency = "ILS"
) => {
  fireFbPixel("AddToWishlist", {
    content_ids: [productId],
    content_name: name,
    content_type: "product",
    value: price,
    currency,
  });

  fireTtqPixel("AddToWishlist", {
    content_id: productId,
    content_type: "product",
    content_name: name,
    value: price,
    currency,
  });

  fireTtqServer("AddToWishlist", {
    content_id: productId,
    content_type: "product",
    content_name: name,
    value: price,
    currency,
  });
};

export const trackSearch = (query: string) => {
  fireFbPixel("Search", { search_string: query });
  fireTtqPixel("Search", { query });
  fireTtqServer("Search", { query });
};

export const trackCompleteRegistration = () => {
  fireFbPixel("CompleteRegistration", {});
  fireTtqPixel("CompleteRegistration", {});
  fireTtqServer("CompleteRegistration", {});
};

export const trackAddPaymentInfo = (
  value: number,
  currency = "ILS"
) => {
  fireFbPixel("AddPaymentInfo", { value, currency });
  fireTtqPixel("AddPaymentInfo", { value, currency });
};
